import { CSV, ExportDigitalAgronomistCropPracticesRequest } from "models";

// Type for field mapping
type FieldMapping = {
  property: keyof ExportDigitalAgronomistCropPracticesRequest;
  type: "boolean" | "number" | "enum" | "string";
};

// Define field mappings and their types
const fieldMappings: Record<string, FieldMapping> = {
  id: { property: "id", type: "string" },
  cropid: { property: "cropId", type: "string" },

  // Boolean fields
  ismaincrop: { property: "isMainCrop", type: "boolean" },
  iscatchcrop: { property: "isCatchCrop", type: "boolean" },
  isundersowingcrop: { property: "isUndersowingCrop", type: "boolean" },
  isfrosttolerant: { property: "isFrostTolerant", type: "boolean" },
  isnfixation: { property: "isNFixation", type: "boolean" },
  isperennial: { property: "isPerennial", type: "boolean" },
  isdroughttolerant: { property: "isDroughtTolerant", type: "boolean" },
  issuitableundersowingfrost: { property: "isSuitableUndersowingFrost", type: "boolean" },
  issuitableundersowingoverwinter: { property: "isSuitableUndersowingOverwinter", type: "boolean" },
  issuitableundersowingperennialgrass: { property: "isSuitableUndersowingPerennialGrass", type: "boolean" },
  issuitableundersowingperenniallegume: { property: "isSuitableUndersowingPerennialLegume", type: "boolean" },

  // Number fields
  minimumgrowtimeweeks: { property: "minimumGrowTimeWeeks", type: "number" },
  minimumgrowtimeweeksinterseedingcrop: { property: "minimumGrowTimeWeeksInterseedingCrop", type: "number" },
  cultivationbreakyears: { property: "cultivationBreakYears", type: "number" },
  firstkwsow: { property: "firstKwSow", type: "number" },
  lastkwharvest: { property: "lastKwHarvest", type: "number" },
  ndemand: { property: "nDemand", type: "number" },
  ndemandcash: { property: "nDemandCash", type: "number" },
  usepossiblefodderrate: { property: "usePossibleFodderRate", type: "number" },
  usepossiblebiogasrate: { property: "usePossibleBiogasRate", type: "number" },
  usepossiblemulchrate: { property: "usePossibleMulchRate", type: "number" },
  lastsowingweek: { property: "lastSowingWeek", type: "number" },
  calendarweekrangemin: { property: "calendarWeekRangeMin", type: "number" },
  calendarweekrangemax: { property: "calendarWeekRangeMax", type: "number" },
  ratelooseningsubsoil: { property: "rateLooseningSubsoil", type: "number" },
  ratecrumblingtopsoil: { property: "rateCrumblingTopsoil", type: "number" },
  ratenutrientbindingfall: { property: "rateNutrientBindingFall", type: "number" },
  ratenutrientbindingspring: { property: "rateNutrientBindingSpring", type: "number" },
  ratebiodiversity: { property: "rateBiodiversity", type: "number" },
  decompositioncnratioplantscore: { property: "decompositionCNRatioPlantScore", type: "number" },
  biomasspotentialabovescore: { property: "biomassPotentialAboveScore", type: "number" },
  biomasspotentialbelowscore: { property: "biomassPotentialBelowScore", type: "number" },
  carbonpotential: { property: "carbonPotential", type: "number" },
  biomasspotentialtotalscore: { property: "biomassPotentialTotalScore", type: "number" },
  youthdevelopment: { property: "youthDevelopment", type: "number" },
  droughtgerminationscore: { property: "droughtGerminationScore", type: "number" },
  droughttolerantscore: { property: "droughtTolerantScore", type: "number" },
  rootstructuretopsoilgoalscore: { property: "rootStructureTopsoilGoalScore", type: "number" },
  rootstructuresubsoilgoalscore: { property: "rootStructureSubsoilGoalScore", type: "number" },
  cultivationbreakbrassica: { property: "cultivationBreakBrassica", type: "number" },
  cultivationbreaknightshades: { property: "cultivationBreakNightshades", type: "number" },
  cultivationbreakchenopodes: { property: "cultivationBreakChenopodes", type: "number" },
  decompositioncnratioplant: { property: "decompositionCNRatioPlant", type: "number" },
  decompositioncnratioroots: { property: "decompositionCNRatioRoots", type: "number" },
  rootstructuretopsoilgoalfactor: { property: "rootStructureTopsoilGoalFactor", type: "number" },
  rootstructuresubsoilgoalfactor: { property: "rootStructureSubsoilGoalFactor", type: "number" },
  biomasspotentialabovedtha: { property: "biomassPotentialAboveDtHa", type: "number" },
  biomasspotentialbelowmindtha: { property: "biomassPotentialBelowMinDtHa", type: "number" },
  biomasspotentialbelowmaxdtha: { property: "biomassPotentialBelowMaxDtHa", type: "number" },
  biomasspotentialbelowaveragedtha: { property: "biomassPotentialBelowAverageDtHa", type: "number" },
  biomasspotentialtotaldtha: { property: "biomassPotentialTotalDtHa", type: "number" },
  droughtgermination: { property: "droughtGermination", type: "number" },
  soilcoverage: { property: "soilCoverage", type: "number" },
  rateweedsupression: { property: "rateWeedSupression", type: "number" },
  mixorsingle: { property: "mixOrSingle", type: "number" },
  winterhardiness: { property: "winterHardiness", type: "number" },
  rootdepthscore: { property: "rootDepthScore", type: "number" },
  silageability: { property: "silageability", type: "number" },
  waterdemand: { property: "waterDemand", type: "number" },
  seedbedrequirement: { property: "seedbedRequirement", type: "number" },
  erosioncontrol: { property: "erosionControl", type: "number" },
  usepossiblegrazingrate: { property: "usePossibleGrazingRate", type: "number" },
  nitrogenaccumulationscore: { property: "nitrogenAccumulationScore", type: "number" },
  killabilityherbicidetillage: { property: "killabilityHerbicideTillage", type: "number" },
  pmobilisation: { property: "pMobilisation", type: "number" },
  pmobilisationgoalscore: { property: "pMobilisationGoalScore", type: "number" },
  germinationspeedfieldemergencemin: { property: "germinationSpeedFieldEmergenceMin", type: "number" },
  germinationspeedfieldemergencemax: { property: "germinationSpeedFieldEmergenceMax", type: "number" },
  nitrogenaccumulationpotentialmin: { property: "nitrogenAccumulationPotentialMin", type: "number" },
  nitrogenaccumulationpotentialmax: { property: "nitrogenAccumulationPotentialMax", type: "number" },
  growingweeksforseedmin: { property: "growingWeeksForSeedMin", type: "number" },
  growingweeksforseedmax: { property: "growingWeeksForSeedMax", type: "number" },
  seedweighttgwmin: { property: "seedWeightTGWMin", type: "number" },
  seedweighttgwmax: { property: "seedWeightTGWMax", type: "number" },
  seedingratekghacovermain: { property: "seedingRateKgHaCoverMain", type: "number" },
  seedingratekghainterseeding: { property: "seedingRateKgHaInterseeding", type: "number" },
  seedcostkg: { property: "seedCostKg", type: "number" },
  seedcosteuroha: { property: "seedCostEuroHa", type: "number" },
  seedingdepthcm: { property: "seedingDepthCm", type: "number" },
  seedingdensityplantssqm: { property: "seedingDensityPlantsSqm", type: "number" },
  rootmaxdepthcm: { property: "rootMaxDepthCm", type: "number" },
  rootaveragedepthcm: { property: "rootAverageDepthCm", type: "number" },
  ndemandlowkgha: { property: "nDemandLowKgHa", type: "number" },
  ndemandhighkgha: { property: "nDemandHighKgHa", type: "number" },
  nitrogenaccumulationpotentialaverage: { property: "nitrogenAccumulationPotentialAverage", type: "number" },
  growingweeksforseedaverage: { property: "growingWeeksForSeedAverage", type: "number" },
  seedsetriskscore: { property: "seedSetRiskScore", type: "number" },
  minsharemix: { property: "minShareMix", type: "number" },
  maxsharemix: { property: "maxShareMix", type: "number" },

  // Enum fields
  ndemandrange: { property: "nDemandRange", type: "enum" },
  croprotation: {
    property: "cropRotation",
    type: "enum",
  },
  stalkleaftype: { property: "stalkLeafType", type: "enum" },
  rootstructure: { property: "rootStructure", type: "enum" },
  soiltolerance: { property: "soilTolerance", type: "enum" },
  undersowingtype: {
    property: "undersowingType",
    type: "enum",
  },
  seedcostrate: { property: "seedCostRate", type: "enum" },
  phtolerance: { property: "phTolerance", type: "enum" },
  season: { property: "season", type: "enum" },
  croprotationsubcategory: {
    property: "cropRotationSubCategory",
    type: "enum",
  },
};

const handleBooleanForStringValues = (value: string): boolean | null | string => {
  if (value.trim() === "") return null;
  if (value?.toLowerCase() === "true") return true;
  if (value?.toLowerCase() === "false") return false;
  return value;
};
const parseFloatOrNull = (value: string): number | null | string => {
  if (value.trim() === "") return null;
  const parsedValue = parseFloat(value);
  return Number.isNaN(parsedValue) ? value : parsedValue;
};

const validateEnum = (value: string): string | null => {
  if (value.trim() === "") return null;
  return value;
};

const parseValue = (
  value: string,
  mapping: FieldMapping
): ExportDigitalAgronomistCropPracticesRequest[keyof ExportDigitalAgronomistCropPracticesRequest] | null => {
  switch (mapping.type) {
    case "boolean":
      return handleBooleanForStringValues(value);
    case "number":
      return parseFloatOrNull(value);
    case "enum":
      return validateEnum(value);
    case "string":
      return value;
    default:
      return value as never;
  }
};

export type ConvertCsvToObjectType = {
  data: ExportDigitalAgronomistCropPracticesRequest[] | null;
  errors: string[];
};
export const ConvertCsvTotDigitalAgronomistCropPracticesRequest = (csv: CSV): ConvertCsvToObjectType => {
  const errorList: string[] = [];
  try {
    const { data, errors } = csv;

    if (errors.length > 0) {
      errorList.push(`CSV parsing errors: ${errors.map((e) => e.message).join(", ")}`);
      return { data: null, errors: errorList };
    }

    // Skip header row and empty rows
    const rows = data.filter((row) => row.length > 0 && row.some((cell) => cell && cell.toString().trim() !== ""));
    if (rows.length === 0) {
      errorList.push("No valid data found in CSV");
      return { data: null, errors: errorList };
    }

    const headers = rows[0];
    const dataRows = rows.slice(1);

    const convertedData: ExportDigitalAgronomistCropPracticesRequest[] = dataRows.map((row) => {
      const rowData: Partial<ExportDigitalAgronomistCropPracticesRequest> = {};

      headers.forEach((header: string, index: number) => {
        const value = row[index] || "";
        const normalizedHeader = header.toLowerCase().trim();
        const mapping = fieldMappings[normalizedHeader];

        if (mapping) {
          // @ts-expect-error should be safe
          rowData[mapping.property] = parseValue(value, mapping);
        }
      });

      return rowData as ExportDigitalAgronomistCropPracticesRequest;
    });
    return { data: convertedData, errors: errorList };
  } catch (err) {
    errorList.push(`Error processing CSV: ${err instanceof Error ? err.message : "Unknown error"}`);
    return { data: null, errors: errorList };
  }
};
